import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search, Sparkles } from 'lucide-react';
import { cn } from '../../utils/tailwindUtils';
import useProcedureCatalog from './useProcedureCatalog';


const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });


/**
 * Masters-backed searchable dropdown for Enquiry.procedure_of_interest.
 *
 * Behaviour:
 *   - Renders as a text input; on focus, a dropdown of catalog entries appears.
 *   - User can type to filter, or click an entry.
 *   - Selecting a catalog entry calls `onPickCatalog(catalog_entry)` — the parent
 *     is expected to set expected_treatment_value and (optionally) department_of_interest.
 *   - User can also type a value not in the catalog (free-text escape hatch).
 *     A subtle "Not in catalog" hint appears while typing a mismatch.
 *
 * Props:
 *   value          — current procedure text
 *   onChange(text) — called on every keystroke / select
 *   onPickCatalog(catalog_row) — called only when the user selects a catalog entry
 *   placeholder    — input placeholder
 *   className      — passed to the outer wrapper
 */
export default function ProcedureSelect({
  value = '',
  onChange,
  onPickCatalog,
  placeholder = 'Search or pick a procedure…',
  className,
}) {
  const { list: procedures, findByName } = useProcedureCatalog();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(value);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Keep filter in sync when parent updates value externally
  useEffect(() => { setFilter(value); }, [value]);

  // Close on outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const q = (filter || '').trim().toLowerCase();
  const filtered = q
    ? procedures.filter(p => (p.name || '').toLowerCase().includes(q) || (p.department || '').toLowerCase().includes(q))
    : procedures;

  const exactMatch = q ? findByName(q) : null;
  const showNoMatchHint = q && !exactMatch && !filtered.some(p => (p.name || '').toLowerCase() === q);

  const handlePick = (row) => {
    setFilter(row.name);
    setOpen(false);
    onChange && onChange(row.name);
    onPickCatalog && onPickCatalog(row);
    // Return focus for keyboard flow
    inputRef.current?.blur();
  };

  const handleType = (v) => {
    setFilter(v);
    onChange && onChange(v);
    if (!open) setOpen(true);
  };

  return (
    <div ref={wrapperRef} className={cn('relative', className)}>
      {/* Input */}
      <div className={cn(
        'flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 focus-within:ring-2 focus-within:ring-blue-500',
      )}>
        <Search className="w-3.5 h-3.5 text-gray-400 ml-2 flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={filter}
          onChange={(e) => handleType(e.target.value)}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="flex-1 min-w-0 bg-transparent px-1 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none"
        />
        <button
          type="button"
          onClick={() => { setOpen(o => !o); inputRef.current?.focus(); }}
          className="p-1 mr-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 dark:text-gray-600"
          tabIndex={-1}
          aria-label="Toggle procedure list"
        >
          <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', open && 'rotate-180')} />
        </button>
      </div>

      {/* Hint below when typing a custom value */}
      {showNoMatchHint && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-0.5">
          "{filter}" isn't in the procedure catalog — will be saved as custom text.
        </p>
      )}
      {exactMatch && (
        <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5 inline-flex items-center gap-1">
          <Sparkles className="w-3 h-3" />
          Matched catalog: {exactMatch.department || '—'} · ₹{formatINR(exactMatch.default_value)}
        </p>
      )}

      {/* Dropdown */}
      {open && (
        <div className="absolute z-40 mt-1 left-0 right-0 max-h-64 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
          {procedures.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              No procedures in catalog yet. Add them via the dashboard's <b>Procedure catalog</b> button.
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              No matches for "{filter}".
            </div>
          ) : (
            <ul className="py-1">
              {filtered.map(p => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => handlePick(p)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:bg-blue-50 dark:focus:bg-blue-900/20 focus:outline-none"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{p.name}</span>
                      <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 flex-shrink-0">
                        ₹ {formatINR(p.default_value)}
                      </span>
                    </div>
                    {(p.department || p.notes) && (
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 truncate">
                        {p.department}{p.notes ? ` · ${p.notes}` : ''}
                      </div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
